"use client"

import { createContext, useContext, useState, useEffect, type ReactNode } from "react"

// Define translations
const translations = {
  en: {
    // Auth
    login: "Login",
    register: "Register",
    emailAddress: "Email Address",
    password: "Password",
    showPassword: "Show password",
    hidePassword: "Hide password",
    forgotPassword: "Forgot Password?",
    loginButton: "Login",
    loading: "Loading...",
    noAccount: "Don't have an account?",
    createAccount: "Create Account",
    fullName: "Full Name",
    phoneNumber: "Phone Number",
    phone: "Phone",
    building: "Building",
    roomNumber: "Apartment Number",
    profilePhoto: "Profile Photo",
    uploadPhoto: "Upload Photo",
    registerButton: "Register",
    alreadyHaveAccount: "Already have an account?",
    resetPassword: "Reset Password",
    backToLogin: "Back to Login",
    registrationComplete: "Registration Complete",
    pendingApprovalMessage: "Your account is pending approval. You'll receive an email when your account is approved.",
    confirmPassword: "Confirm Password",
    firstName: "First Name",
    lastName: "Last Name",
    registrationCode: "Registration Code",
    enterRegistrationCode: "Enter registration code",
    registrationCodeHelp: "Enter the registration code provided by your building management",

    // Dashboard
    dashboard: "Dashboard",
    tickets: "Tickets",
    messages: "Messages",
    announcements: "Announcements",
    blog: "Blog",
    availableRooms: "Available Apartments",
    membershipCard: "Membership Card",
    discounts: "Discounts",
    userManagement: "User Management",
    ticketManagement: "Ticket Management",
    announcementManagement: "Announcement Management",
    blogManagement: "Blog Management",
    roomManagement: "Room Management",
    discountManagement: "Discount Management",
    buildingManagement: "Building Management",
    adminSection: "Admin Section",
    settings: "Settings",
    logout: "Logout",
    welcomeMessage: "Welcome",
    enableNotifications: "Enable Notifications",
    openTickets: "Open",
    closedTickets: "Closed",
    unreadMessages: "Unread Messages",
    newAnnouncements: "New Announcements",
    newDiscounts: "New Discounts",
    quickActions: "Quick Actions",
    createTicket: "Create Ticket",
    newMessage: "New Message",
    viewAnnouncements: "View Announcements",
    viewDiscounts: "View Discounts",
    pendingApprovals: "Pending",
    manageRooms: "Manage Apartments",
    recentActivity: "Recent Activity",
    ticketUpdated: "Ticket {{id}} Updated",
    ticketStatusChanged: "Status changed to 'In Progress'",
    profile: "Profile",

    // Tickets
    newTicket: "New Ticket",
    newTicketDescription: "Please provide details about your issue",
    ticketType: "Ticket Type",
    selectTicketType: "Select ticket type",
    electrical: "Electrical",
    plumbing: "Plumbing",
    internet: "Internet",
    appliances: "Appliances",
    commonAreas: "Common Areas",
    other: "Other",
    ticketDescription: "Description",
    ticketDescriptionPlaceholder: "Please describe your issue in detail...",
    uploadImage: "Upload Image",
    selectImage: "Select Image",
    cancel: "Cancel",
    submitting: "Submitting...",
    submitTicket: "Submit Ticket",
    ticketCreatedDescription: "Your ticket has been created successfully!",
    ticketCreationFailedDescription: "Failed to create ticket. Please try again.",
    noOpenTickets: "No open tickets",
    noClosedTickets: "No closed tickets",
    open: "Open",
    closed: "Closed",
    status: "Status",
    date: "Date",
    type: "Type",
    ticketNotFound: "Ticket not found",
    submittedBy: "Submitted by",
    attachment: "Attachment",
    viewTicket: "View Ticket",
    closeTicket: "Close Ticket",
    deleteTicket: "Delete Ticket",
    totalTickets: "Total Tickets",
    maintenance: "Maintenance",
    complaint: "Complaint",
    request: "Request",
    viewConversation: "View Conversation",
    showFullConversation: "Show Full Conversation",
    conversationHasMessages: "This conversation has {count} messages",

    // Messages
    searchMessages: "Search messages...",
    conversations: "Conversations",
    noConversations: "No conversations found",
    observedConversations: "Observed Conversations",
    observer: "Observer",
    observerModeMessage: "You are viewing this conversation as an observer. You cannot reply.",
    noObservedConversations: "No observed conversations in this period",
    showMore: "Show more",
    showAll: "Show all",
    from: "From",
    clear: "Clear",
    composeMessage: "Compose Message",
    to: "To",
    selectRecipient: "Select recipient",
    searchByNameOrApartment: "Search by name or apartment number...",
    apartment: "Apartment",
    message: "Message",
    typeYourMessage: "Type your message here...",
    typeMessage: "Type a message...",
    sending: "Sending...",
    sendMessage: "Send Message",
    messageSentDescription: "Your message has been sent successfully!",
    messageErrorDescription: "Failed to send message. Please try again.",
    back: "Back",
    conversation: "Conversation",
    noCommentsYet: "No comments yet",
    addComment: "Add comment",
    comments: "comments",
    noComments: "No comments",
    subject: "Subject",
    messagesSent: "Message sent successfully!",

    // Announcements
    noAnnouncements: "No announcements available",
    newAnnouncement: "New Announcement",
    title: "Title",
    content: "Content",
    buildings: "Buildings",
    enterTitle: "Enter title",
    enterContent: "Enter content",
    create: "Create",
    update: "Update",
    noAnnouncementsFound: "No announcements found",
    searchAnnouncements: "Search announcements...",
    editAnnouncement: "Edit Announcement",
    deleteAnnouncement: "Delete Announcement",
    announcementImage: "Image",
    uploadImage: "Upload Image",
    removeImage: "Remove Image",
    changeImage: "Change Image",

    // Blog
    allCategories: "All Categories",
    sustainability: "Sustainability",
    events: "Events",
    safety: "Safety",
    local: "Local",
    newArticle: "New Article",
    manageArticles: "Manage Articles",
    noArticles: "No articles available",
    readMore: "Read More",
    articleNotFound: "Article not found",
    backToBlog: "Back to Blog",

    // Rooms
    availableApartments: "Available Apartments",
    availableApartmentsDescription: "Browse available apartments in our buildings",
    allBuildings: "All Buildings",
    allTypes: "All Types",
    studio: "Studio",
    oneBedroom: "1 Bedroom",
    twoBedroom: "2 Bedroom",
    size: "Size",
    price: "Price",
    availableFrom: "Available From",
    actions: "Actions",
    inquire: "Inquire",
    noRoomsFound: "No apartments found matching your criteria",
    addRoom: "Add Apartment",
    room: "Apartment",
    viewDetails: "View details",

    // Membership Card
    memberSince: "Member Since",
    memberID: "Member ID",
    membershipCardDescription: "Present this card at any partner location to unlock your exclusive Unity discount.",
    membershipCardDisclaimer:
      "This card is for identification purposes only and remains the property of UnitySmartLiving.",

    // Discounts
    businessDiscounts: "Business Discounts",
    cafes: "Cafes",
    restaurants: "Restaurants",
    gyms: "Gyms",
    retail: "Retail",
    services: "Services",
    noBusinessesFound: "No businesses found",
    targetBuilding: "Target Building",
    noBuildings: "No buildings",
    businessName: "Business Name",
    businessCategory: "Business Category",
    businessAddress: "Business Address",
    businessPhone: "Business Phone",
    businessDiscount: "Discount Offer",
    businessLogo: "Business Logo",
    availableInBuildings: "Available In Buildings",
    selectBuildings: "Select Buildings",
    businessDetails: "Business Details",
    confirmDeleteBusiness: "Are you sure you want to delete this business?",
    businessAdded: "Business added successfully",
    businessUpdated: "Business updated successfully",
    businessDeleted: "Business deleted successfully",
    failedToAddBusiness: "Failed to add business",
    failedToUpdateBusiness: "Failed to update business",
    failedToDeleteBusiness: "Failed to delete business",

    // Notifications
    notifications: "Notifications",
    markAllAsRead: "Mark all as read",
    noNotifications: "No notifications",
    notificationPermissionGranted: "Notification permission granted",
    notificationPermissionDenied: "Notification permission denied",

    // Admin
    totalUsers: "Total Users",
    allUsers: "All",
    approvedUsers: "Approved",
    disabledUsers: "Disabled",
    duplicateUsers: "Duplicates",
    duplicateUsersHelp: "Active tenants registered to the same apartment, usually an old account that should be disabled",
    enableUser: "Re-enable user",
    enableUserConfirmation: "Re-enable user '{{name}}'? They will be able to sign in again.",
    userEnabledMessage: "User has been re-enabled successfully",
    failedToEnableUser: "Failed to re-enable user",
    allTickets: "All",
    searchUsers: "Search users...",
    searchTickets: "Search tickets...",
    name: "Name",
    email: "Email",
    role: "Role",
    user: "User",
    admin: "Admin",
    approved: "Approved",
    lastReply: "Last Reply",
    noone: "Noone",
    approveUser: "Approve User",
    editUser: "Edit User",
    deleteUser: "Delete User",
    save: "Save",
    noUsersFound: "No users found",
    ticketId: "Ticket ID",
    description: "Description",
    noTicketsFound: "No tickets found",
    view: "View",
    reopenTicket: "Reopen Ticket",
    editArticle: "Edit Article",
    deleteArticle: "Delete Article",
    searchArticles: "Search articles...",
    enterPreview: "Enter preview",
    selectCategory: "Select category",
    thumbnail: "Thumbnail",
    preview: "Preview",
    editRoom: "Edit Apartment",
    deleteRoom: "Delete Apartment",
    searchRooms: "Search apartments...",
    selectBuilding: "Select building",
    selectType: "Select type",
    editBusiness: "Edit Business",
    deleteBusiness: "Delete Business",
    searchBusinesses: "Search businesses...",
    enterBusinessName: "Enter business name",
    enterAddress: "Enter address",
    enterPhone: "Enter phone number",
    enterDiscount: "Enter discount",
    logo: "Logo",
    addBusiness: "Add Business",
    failedToApproveUser: "Failed to approve user",
    failedToDeleteUser: "Failed to delete user",
    failedToUpdateUser: "Failed to update user",
    userUpdatedMessage: "User updated successfully",
    confirmDeleteUser: "Are you sure you want to delete this user?",
    saving: "Saving...",
    managedBuildings: "Managed Buildings",
    canReceiveMessages: "Can Receive Messages",
    canReceiveMessagesHelp: "Allow this admin to receive messages from users",
    managedBuildingsHelp: "Select which buildings this admin can manage",
    managedTicketCategories: "Managed Ticket Categories",
    managedTicketCategoriesHelp:
      "Select which ticket categories this admin can manage. Leave empty to allow all categories.",
    adminDashboard: "Admin Dashboard",
    manageBuildings: "Manage Buildings",
    registrationSettings: "Registration Settings",
    registrationCodeAdminHelp:
      "This code will be required for new user registrations. Code comparison is case-insensitive.",

    // Settings
    profileInformation: "Profile Information",
    saveChanges: "Save Changes",
    updating: "Updating...",
    savePhoto: "Save Photo",

    // Landing Page
    landingTitle: "Smart Living for Modern Communities",
    landingDescription:
      "Manage your apartment living experience with ease. From maintenance requests to community announcements, everything you need in one place.",
    getStarted: "Get Started",
    existingUser: "Existing User",
    featuresTitle: "Features",
    featuresDescription:
      "Discover how UnitySmartLiving can enhance your living experience with these powerful features.",
    featureTicketsTitle: "Maintenance Tickets",
    featureTicketsDescription: "Submit and track maintenance requests easily.",
    featureMessagingTitle: "Direct Messaging",
    featureMessagingDescription: "Communicate directly with building management.",
    featureAnnouncementsTitle: "Community Announcements",
    featureAnnouncementsDescription: "Stay updated with important building news.",
    featureBlogTitle: "Community Blog",
    featureBlogDescription: "Read articles and updates about your community.",
    featureRoomsTitle: "Available Apartments",
    featureRoomsDescription: "Browse available apartments in your building.",
    featureDiscountsTitle: "Local Discounts",
    featureDiscountsDescription: "Enjoy special offers from local businesses.",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    contact: "Contact Us",

    // General
    searchFilter: "Search filter",
    apply: "Apply",
    filter: "Filter",
    uploadImageDescription: "Upload an image to provide more details about your issue",
    attachImage: "Attach Image",
    fileSelected: "File selected",
    confirmDelete: "Are you sure you want to delete this item?",
    yes: "Yes",
    no: "No",
    success: "Success",
    error: "Error",
    warning: "Warning",
    info: "Info",

    // Dashboard
    manageUsers: "Manage Users",
    manageTickets: "Manage Tickets",
    manageAnnouncements: "Manage Announcements",
    manageBlog: "Manage Blog",
    manageRoomsTitle: "Manage Apartments",
    manageDiscounts: "Manage Discounts",
    recentActivities: "Recent Activities",
    allActivities: "All Activities",
    viewAll: "View All",
    all: "All",
    accountSettings: "Account Settings",
    personalInformation: "Personal Information",
    emailAddressLabel: "Email Address",
    phoneNumberLabel: "Phone Number",

    // Tickets
    ticketDetails: "Ticket Details",
    ticketStatus: "Ticket Status",
    ticketHistory: "Ticket History",
    commentContent: "Comment content",
    submitComment: "Submit Comment",
    ticketImages: "Ticket Images",

    // Messages
    messageDetails: "Message Details",
    replyToMessage: "Reply to Message",
    markAsRead: "Mark as Read",
    markAsUnread: "Mark As Unread",
    deleteConversation: "Delete Conversation",
    newConversation: "New Conversation",
    messageManagement: "Message Management",

    // Rooms
    buildingLabel: "Building",
    roomTypeLabel: "Apartment Type",
    roomSizeLabel: "Apartment Size",
    roomPriceLabel: "Apartment Price",
    availabilityDate: "Availability Date",
    inquireAboutRoom: "Inquire About Apartment",
    inquiryDetails: "Inquiry Details",
    inquirySent: "Inquiry Sent",
    submitInquiry: "Submit Inquiry",

    // Notification Messages
    notificationSuccessTitle: "Operation Successful",
    notificationErrorTitle: "Operation Failed",
    userApprovedMessage: "User has been approved successfully",
    userDeletedMessage: "User has been deleted successfully",
    profileUpdatedMessage: "Profile updated successfully",
    passwordChangedMessage: "Password changed successfully",
    photoUpdatedMessage: "Profile photo updated successfully",
    roomAddedMessage: "Apartment added successfully",
    roomUpdatedMessage: "Apartment updated successfully",
    roomDeletedMessage: "Apartment deleted successfully",
    ticketSubmittedMessage: "Ticket submitted successfully",
    ticketUpdatedMessage: "Ticket updated successfully",
    ticketClosedMessage: "Ticket closed successfully",
    ticketReopenedMessage: "Ticket reopened successfully",
    commentAddedMessage: "Comment added successfully",

    // Form Validation
    requiredField: "This field is required",
    invalidEmail: "Please enter a valid email address",
    passwordTooShort: "Password must be at least 8 characters long",
    passwordsMustMatch: "Passwords must match",
    invalidPhoneNumber: "Please enter a valid phone number",
    required: "This field is required",
    passwordsDoNotMatch: "Passwords do not match",

    // Admin Operations
    changeUserRole: "Change User Role",
    approveSelectedUsers: "Approve Selected Users",
    bulkActions: "Bulk Actions",
    createNewAnnouncement: "Create New Announcement",
    createNewArticle: "Create New Article",
    publishArticle: "Publish Article",
    saveDraft: "Save Draft",
    previewArticle: "Preview Article",
    targetBuildings: "Target Buildings",
    notifyUsers: "Notify Users",
    sendNotification: "Send Notification",
    notificationSent: "Notification sent successfully",
    exportData: "Export Data",
    importData: "Import Data",
    systemSettings: "System Settings",
    emailTemplates: "Email Templates",

    // Reports and Statistics
    statistics: "Statistics",
    reports: "Reports",
    occupancyRate: "Occupancy Rate",
    ticketResolutionTime: "Ticket Resolution Time",
    activeUsers: "Active Users",
    newRegistrations: "New Registrations",
    monthlyReport: "Monthly Report",
    yearlyReport: "Yearly Report",
    customReport: "Custom Report",
    generateReport: "Generate Report",
    exportToPDF: "Export to PDF",
    exportToCSV: "Export to CSV",
    dataRange: "Data Range",
    chartType: "Chart Type",
    pieChart: "Pie Chart",
    barChart: "Bar Chart",
    lineChart: "Line Chart",

    // Memberships and Payments
    membershipDetails: "Membership Details",
    membershipLevel: "Membership Level",
    membershipBenefits: "Membership Benefits",
    paymentHistory: "Payment History",
    makePayment: "Make Payment",
    paymentMethod: "Payment Method",
    creditCard: "Credit Card",
    bankTransfer: "Bank Transfer",
    paypal: "PayPal",
    paymentAmount: "Payment Amount",
    paymentDate: "Payment Date",
    paymentStatus: "Payment Status",
    paid: "Paid",
    pending: "Pending",
    failed: "Failed",
    refunded: "Refunded",
    invoices: "Invoices",
    viewInvoice: "View Invoice",
    downloadInvoice: "Download Invoice",
    billingAddress: "Billing Address",
    subscriptionPlan: "Subscription Plan",
    changeSubscription: "Change Subscription",
    cancelSubscription: "Cancel Subscription",

    // Calendar and Events
    calendar: "Calendar",
    upcomingEvents: "Upcoming Events",
    createEvent: "Create Event",
    eventTitle: "Event Title",
    eventDescription: "Event Description",
    eventDate: "Event Date",
    startTime: "Start Time",
    endTime: "End Time",
    eventLocation: "Event Location",
    eventType: "Event Type",
    communityEvent: "Community Event",
    privateMeeting: "Private Meeting",
    maintenanceWork: "Maintenance Work",
    allDayEvent: "All Day Event",
    recurringEvent: "Recurring Event",
    eventParticipants: "Event Participants",
    inviteParticipants: "Invite Participants",
    rsvp: "RSVP",
    attending: "Attending",
    notAttending: "Not Attending",
    maybeAttending: "Maybe Attending",

    // Buildings
    addBuilding: "Add Building",
    editBuilding: "Edit Building",
    archiveBuilding: "Archive Building",
    unarchiveBuilding: "Unarchive Building",
    showArchived: "Show Archived",
    noBuildingsFound: "No buildings found",
    enterBuildingName: "Enter building name",
    enterCity: "Enter city",
    enterZipCode: "Enter zip code",
    enterTotalRooms: "Enter total number of apartments",
    totalRooms: "Total Apartments",
    zipCode: "Zip Code",
    city: "City",
    active: "Active",
    archived: "Archived",
    allStatuses: "All Statuses",
    disabled: "Disabled",
    businessActive: "Business Active",
    enableBusiness: "Enable Business",
    disableBusiness: "Disable Business",
    attachFile: "Attach File",
    notificationOptions: "Notification Options",
    sendEmailNotification: "Send Email Notification",
    enablePushNotification: "Enable Push Notification",
    pushNotificationInfo: "Push notifications will be sent to all users in the selected buildings.",
    emailNotificationInfo: "Email notifications will be sent to all users in the selected buildings.",
    showExpandedOnHome: "Show fully expanded on home",
    expandAnnouncement: "Show full announcement",
    collapseAnnouncement: "Collapse announcement",
    showLess: "Show less",
    category: "Category",
    address: "Address",
    discount: "Discount",

    searchBuildings: "Search buildings",
    profilePhotoRequired: "Profile photo is required",

    // Logout confirmation
    confirmLogout: "Are you sure you want to logout?",
    areYouSureLogout: "Are you sure you want to logout?",

    // Message status
    unread: "Unread",

    // Delete confirmations
    deleteConversationConfirmation: "Are you sure you want to delete this conversation?",
    deleteTicketConfirmation: "Are you sure you want to delete this ticket '{{title}}'?",
    deleteAnnouncementConfirmation: "Are you sure you want to delete this announcement '{{title}}'?",
    deleteArticleConfirmation: "Are you sure you want to delete this article '{{title}}'?",
    deleteBusinessConfirmation: "Are you sure you want to delete this business '{{name}}'?",
    deleteUserConfirmation: "Are you sure you want to delete user '{{name}}'?",
    archiveBuildingConfirmation: "Are you sure you want to archive building '{{name}}'?",
    unarchiveBuildingConfirmation: "Are you sure you want to unarchive building '{{name}}'?",

    // General actions
    delete: "Delete",
    archive: "Archive",
    unarchive: "Unarchive",
    confirmArchive: "Confirm Archive",
    confirmUnarchive: "Confirm Unarchive",

    // Password Reset
    passwordResetEmailSent: "Password reset email sent successfully. Please check your email for instructions.",
    failedToSendResetEmail: "Failed to send password reset email",
    noAccountFound: "No account found with this email address",
    tooManyRequests: "Too many password reset requests. Please try again later",
    networkError: "Network error. Please check your connection and try again",
    serverError: "Server error. Please try again later.",
    unauthorized: "You are not authorized to perform this action.",

    // Account page
    account: "Account",
    accountInformation: "Account Information",
    deleteMyAccount: "Delete my account",
    deleteAccountConfirmation: "Delete Account",
    deleteAccountWarning: "Are you sure you want to request account deletion? This action cannot be undone.",
    deleteAccountProcess: "Admins will be notified and will contact you within 24 hours to process your request.",
    requestAccountDeletion: "Request Account Deletion",
    requestSubmitted: "Request Submitted",
    accountDeletionRequestMessage: "Admins have been informed and you will be contacted in the next 24 hours.",
    failedToRequestAccountDeletion: "Failed to submit account deletion request",
    notProvided: "Not provided",
    pendingApproval: "Pending Approval",
    accountPendingMessage:
      "Your account is pending approval. Please wait for an administrator to approve your registration.",
    contactAdmin: "Contact Administrator",

    members: "members",

    somethingWentWrong: "Something went wrong",
    unknownError: "Unknown error",
    loginSuccessful: "Login successful! Welcome back to UnitySmartLiving!",
    invalidEmailOrPassword: "Invalid email or password",
    registrationSuccessful: "Registration successful! Your account is pending approval.",
    maxDimensions: "Max dimensions",
    switchLanguage: "Switch language",

    emailPlaceholder: "name@example.com",
    roomSizePlaceholder: "45m²",

    newMessageNotification: "New Message",
    newMessageContent: "You have a new message from Admin",
    ticketUpdateNotification: "Ticket Update",
    ticketUpdateContent: "Your maintenance ticket has been updated",
    buildingAnnouncementNotification: "Building Announcement",
    buildingAnnouncementContent: "Important: Maintenance scheduled for tomorrow",

    adminRole: "Admin",
    adminUsers: "Admin Users",
    maintenanceTeam: "Maintenance Team",
    buildingManager: "Building Manager",
    securityTeam: "Security Team",

    // Attachment Preview Modal
    download: "Download",
    close: "Close",
    fileType: "File Type",
    previewNotAvailable: "Preview not available",
    downloadToView: "Download to view",

    // Guest Registration
    guestRegistration: "Guest Registration",
    guestManagement: "Guest Management",
    registerGuest: "Register Guest",
    parents: "Parents",
    guestFullName: "Full Name",
    guestIdNumber: "ID Number",
    checkInDate: "Check-in Date",
    checkOutDate: "Check-out Date",
    createdAt: "Created",
    checkOutAfterCheckIn: "Check-out date must be after check-in date",
    checkInPastWarning: "Warning: The check-in date is in the past.",
    totalDays: "Total Days",
    daysUsedThisMonth: "Days Used This Month",
    monthlyLimit: "Monthly Limit",
    daysPerMonth: "days/month",
    guestHistory: "Guest History",
    noGuestsFound: "No guest registrations found",
    guestRegisteredSuccessfully: "Guest registered successfully",
    guestRegistrationFailed: "Failed to register guest",
    guestDeletedSuccessfully: "Guest registration deleted",
    limitWarning: "Warning: This registration will bring your total to {{total}} days this month, exceeding the limit of {{limit}} days. Extra charges may apply.",
    historyDisabledMessage: "Guest history is not available for this building.",
    guestSettings: "Guest Settings",
    monthlyDayLimit: "Monthly Day Limit",
    zeroMeansNoLimit: "0 = no limit",
    showLimitWarning: "Show Limit Warning to Residents",
    showGuestHistory: "Show Guest History to Residents",
    saveSettings: "Save Settings",
    settingsSaved: "Settings saved successfully",
    settingsSaveFailed: "Failed to save settings",
    notificationSettings: "Notification Settings",
    muteGuestNotifications: "Mute Guest Registration Notifications",
    muteGuestNotificationsHelp: "When enabled, you will not receive email or push notifications for new guest registrations, limit exceeded alerts, or guest comments.",
    disableEmailNotifications: "Disable Email Notifications",
    disableEmailNotificationsHelp: "When enabled, this admin will not receive any email notifications. Push notifications are still sent.",
    selectMonth: "Select Month",
    monthlySummary: "Monthly Summary",
    totalGuests: "Total Guests",
    apartment: "Apartment",
    resident: "Resident",
    numberOfGuests: "Number of Guests",
    guestRegistrations: "Guest Registrations",
    exceedsLimit: "exceeds limit",
    selectBuildingFirst: "Please select a building first",
    allFieldsRequired: "All fields are required",
    notes: "Notes",
    notesPlaceholder: "Optional notes about the guest...",
    allApartments: "All Apartments",
    showOverlapsOnly: "Show Overlaps Only",
    overlappingBooking: "Overlapping booking",
    overlapWarning: "Warning: This apartment already has a guest registered for overlapping dates.",
    thread: "Thread",
    openThread: "Open Thread",
    noCommentsYet: "No comments yet",
    startConversation: "Start the conversation",
    addComment: "Add Comment",
    typeYourMessage: "Type your message",
    conversation: "Conversation",
    guestNotFound: "Guest registration not found",
    days: "days",
    // SoftOne links (admin only)
    softoneColumn: "SoftOne",
    softoneLinkLabel: "SoftOne link",
    softonePending: "Pending",
    softonePendingOption: "— Pending (not decided yet) —",
    softoneNone: "No SoftOne link",
    softoneNoneShort: "No link",
    softoneSuggested: "Suggested",
    softoneUseSuggestion: "Use suggestion",
    softoneDetailCode: "Code",
    softoneDetailName: "Description",
    softoneDetailContract: "Contract",
    softoneDetailMatch: "Match",
    softoneDetailSource: "Linked via",
    softoneDetailBy: "By",
    softoneDetailAt: "When",
    softoneSource_suggestion: "approved suggestion",
    softoneSource_manual: "manual choice",
    softoneSource_backfill: "bulk linking",
    softoneChoose: "Choose",
    softoneSearchPlaceholder: "Search by code or name...",
    softoneNoResults: "No SoftOne records found",
    softoneApproveSuggestion: "Approve suggestion",
    softoneConfidence_exact: "exact match",
    softoneConfidence_normalized: "normalized match",
    softoneConfidence_fuzzy: "approximate — please check",
    softoneStale: "The user's building or apartment number changed after linking, or the apartment no longer exists in SoftOne. Please check.",
    softoneMissing: "The linked building no longer exists in SoftOne. Please check.",
    softoneBuildingNotLinked: "Link the user's building to SoftOne first (Building Management).",
    softoneBuildingNoLink: "The user's building is marked as not linked to SoftOne.",
    softoneLoading: "Loading SoftOne data...",
    softoneLoadError: "Could not load SoftOne data",
    softoneLinkSaved: "SoftOne link saved",
    softoneLinkSaveError: "Failed to save SoftOne link",
    softoneAdminOnlyHelp: "Visible to admins only.",
    softoneFilterAll: "SoftOne: all",
    softoneFilterLinked: "Linked",
    softoneFilterSuggested: "With suggestion",
    softoneFilterPending: "Pending, no suggestion",
    softoneFilterNone: "No link",
    softoneFilterStale: "Needs check",
    softoneRequestColumn: "SoftOne",
    softoneRequestSent: "Request #{{id}}",
    softoneRequestSending: "Sending...",
    softoneRequestFailed: "Failed",
    softoneRequestWaiting: "No apartment",
    softoneRequestWaitingHelp: "The user has no linked SoftOne apartment. Link it in User Management, then send again.",
    softoneRequestDisabled: "Not sent",
    softoneRequestDisabledHelp: "Sending to SoftOne was turned off when this ticket was created.",
    softoneRequestSend: "Send to SoftOne",
    softoneRequestSentToast: "Sent to SoftOne",
    softoneRequestSendError: "Failed to send to SoftOne",
  },
  el: {
    // Auth
    login: "Σύνδεση",
    register: "Εγγραφή",
    emailAddress: "Διεύθυνση Email",
    password: "Κωδικός",
    showPassword: "Εμφάνιση κωδικού",
    hidePassword: "Απόκρυψη κωδικού",
    forgotPassword: "Ξεχάσατε τον κωδικό;",
    loginButton: "Σύνδεση",
    loading: "Φόρτωση...",
    noAccount: "Δεν έχετε λογαριασμό;",
    createAccount: "Δημιουργία Λογαριασμού",
    fullName: "Ονοματεπώνυμο",
    phoneNumber: "Τηλέφωνο",
    phone: "Τηλέφωνο",
    building: "Κτίριο",
    roomNumber: "Αριθμός Διαμερίσματος",
    profilePhoto: "Φωτογραφία Προφίλ",
    uploadPhoto: "Ανέβασμα Φωτογραφίας",
    registerButton: "Εγγραφή",
    alreadyHaveAccount: "Έχετε ήδη λογαριασμό;",
    resetPassword: "Επαναφορά Κωδικού",
    backToLogin: "Επιστροφή στη Σύνδεση",
    registrationComplete: "Η Εγγραφή Ολοκληρώθηκε",
    pendingApprovalMessage: "Ο λογαριασμός σας αναμένει έγκριση. Θα λάβετε email όταν εγκριθεί.",
    confirmPassword: "Επιβεβαίωση Κωδικού",
    firstName: "Όνομα",
    lastName: "Επώνυμο",
    registrationCode: "Κωδικός Εγγραφής",
    enterRegistrationCode: "Εισάγετε κωδικό εγγραφής",
    registrationCodeHelp: "Εισάγετε τον κωδικό εγγραφής που σας παρείχε η διαχείριση του κτιρίου",

    // Dashboard
    dashboard: "Πίνακας Ελέγχου",
    tickets: "Αιτήματα",
    messages: "Μηνύματα",
    announcements: "Ανακοινώσεις",
    blog: "Ιστολόγιο",
    availableRooms: "Διαθέσιμα Διαμερίσματα",
    membershipCard: "Κάρτα Μέλους",
    discounts: "Εκπτώσεις",
    userManagement: "Διαχείριση Χρηστών",
    ticketManagement: "Διαχείριση Αιτημάτων",
    announcementManagement: "Διαχείριση Ανακοινώσεων",
    blogManagement: "Διαχείριση Ιστολογίου",
    roomManagement: "Διαχείριση Διαμερισμάτων",
    discountManagement: "Διαχείριση Εκπτώσεων",
    buildingManagement: "Διαχείριση Κτιρίων",
    adminSection: "Ενοτητα Διαχειριστη",
    settings: "Ρυθμίσεις",
    logout: "Αποσύνδεση",
    welcomeMessage: "Καλωσήρθατε",
    enableNotifications: "Ενεργοποίηση Ειδοποιήσεων",
    openTickets: "Ανοιχτά",
    closedTickets: "Κλειστά",
    unreadMessages: "Μη Αναγνωσμένα Μηνύματα",
    newAnnouncements: "Νέες Ανακοινώσεις",
    newDiscounts: "Νέες Εκπτώσεις",
    quickActions: "Γρήγορες Ενέργειες",
    createTicket: "Δημιουργία Αιτήματος",
    newMessage: "Νέο Μήνυμα",
    viewAnnouncements: "Προβολή Ανακοινώσεων",
    viewDiscounts: "Προβολή Εκπτώσεων",
    pendingApprovals: "Εκκρεμείς",
    manageRooms: "Διαχείριση Διαμερισμάτων",
    recentActivity: "Πρόσφατη Δραστηριότητα",
    ticketUpdated: "Το Αίτημα {{id}} Ενημερώθηκε",
    ticketStatusChanged: "Η κατάσταση άλλαξε σε 'Σε Εξέλιξη'",
    profile: "Προφίλ",

    // Tickets
    newTicket: "Νέο Αίτημα",
    newTicketDescription: "Παρακαλώ παρέχετε λεπτομέρειες για το πρόβλημά σας",
    ticketType: "Τύπος Αιτήματος",
    selectTicketType: "Επιλέξτε τύπο αιτήματος",
    electrical: "Ηλεκτρολογικά",
    plumbing: "Υδραυλικά",
    internet: "Διαδίκτυο",
    appliances: "Συσκευές",
    commonAreas: "Κοινόχρηστοι Χώροι",
    other: "Άλλο",
    ticketDescription: "Περιγραφή",
    ticketDescriptionPlaceholder: "Παρακαλώ περιγράψτε το πρόβλημά σας με λεπτομέρεια...",
    uploadImage: "Ανέβασμα Εικόνας",
    selectImage: "Επιλογή Εικόνας",
    cancel: "Ακύρωση",
    submitting: "Υποβολή...",
    submitTicket: "Υποβολή Αιτήματος",
    ticketCreatedDescription: "Το αίτημά σας δημιουργήθηκε με επιτυχία!",
    ticketCreationFailedDescription: "Αποτυχία δημιουργίας αιτήματος. Παρακαλώ δοκιμάστε ξανά.",
    noOpenTickets: "Δεν υπάρχουν ανοιχτά αιτήματα",
    noClosedTickets: "Δεν υπάρχουν κλειστά αιτήματα",
    open: "Ανοιχτό",
    closed: "Κλειστό",
    status: "Κατάσταση",
    date: "Ημερομηνία",
    type: "Τύπος",
    ticketNotFound: "Το αίτημα δεν βρέθηκε",
    submittedBy: "Υποβλήθηκε από",
    attachment: "Συνημμένο",
    viewTicket: "Προβολή Αιτήματος",
    closeTicket: "Κλείσιμο Αιτήματος",
    deleteTicket: "Διαγραφή Αιτήματος",
    totalTickets: "Συνολικά Αιτήματα",
    maintenance: "Συντήρηση",
    complaint: "Απόφαση",
    request: "Αίτημα",
    viewConversation: "Προβολή Συνομιλίας",
    showFullConversation: "Εμφάνιση Πλήρους Συνομιλίας",
    conversationHasMessages: "Αυτή η συνομιλία έχει {count} μηνύματα",

    // Messages
    searchMessages: "Αναζήτηση μηνυμάτων...",
    conversations: "Συνομιλίες",
    noConversations: "Δεν βρέθηκαν συνομιλίες",
    observedConversations: "Παρατηρούμενες Συνομιλίες",
    observer: "Παρατηρητής",
    observerModeMessage: "Βλέπετε αυτή τη συνομιλία ως παρατηρητής. Δεν μπορείτε να απαντήσετε.",
    noObservedConversations: "Δεν υπάρχουν παρατηρούμενες συνομιλίες σε αυτήν την περίοδο",
    showMore: "Περισσότερα",
    showAll: "Εμφάνιση όλων",
    from: "Από",
    clear: "Καθαρισμός",
    composeMessage: "Σύνταξη Μηνύματος",
    to: "Προς",
    selectRecipient: "Επιλέξτε παραλήπτη",
    searchByNameOrApartment: "Αναζήτηση με όνομα ή αριθμό διαμερίσματος...",
    apartment: "Διαμέρισμα",
    message: "Μήνυμα",
    typeYourMessage: "Γράψτε το μήνυμά σας εδώ...",
    typeMessage: "Γράψτε ένα μήνυμα...",
    sending: "Αποστολή...",
    sendMessage: "Αποστολή Μηνύματος",
    messageSentDescription: "Το μήνυμά σας στάλθηκε με επιτυχία!",
    messageErrorDescription: "Αποτυχία αποστολής μηνύματος. Παρακαλώ δοκιμάστε ξανά.",
    back: "Πίσω",
    conversation: "Συνομιλία",
    noCommentsYet: "Δεν υπάρχουν σχόλια ακόμα",
    addComment: "Προσθήκη σχολίου",
    comments: "σχόλια",
    noComments: "Δεν υπάρχουν σχόλια",
    subject: "Θέμα",
    messagesSent: "Το μήνυμά σας στάλθηκε με επιτυχία!",

    // Announcements
    noAnnouncements: "Δεν υπάρχουν διαθέσιμες ανακοινώσεις",
    newAnnouncement: "Νέα Ανακοίνωση",
    title: "Τίτλος",
    content: "Περιεχόμενο",
    buildings: "Κτίρια",
    enterTitle: "Εισάγετε τίτλο",
    enterContent: "Εισάγετε περιεχόμενο",
    create: "Δημιουργία",
    update: "Ενημέρωση",
    noAnnouncementsFound: "Δεν βρέθηκαν ανακοινώσεις",
    searchAnnouncements: "Αναζήτηση ανακοινώσεων...",
    editAnnouncement: "Επεξεργασία Ανακοίνωσης",
    deleteAnnouncement: "Διαγραφή Ανακοίνωσης",
    announcementImage: "Φωτογραφία",
    uploadImage: "Μεταφόρτωση Φωτογραφίας",
    removeImage: "Αφαίρεση Φωτογραφίας",
    changeImage: "Αλλαγή Φωτογραφίας",

    // Blog
    allCategories: "Όλες οι Κατηγορίες",
    sustainability: "Βιωσιμότητα",
    events: "Εκδηλώσεις",
    safety: "Ασφάλεια",
    local: "Τοπικά",
    newArticle: "Νέο Άρθρο",
    manageArticles: "Διαχείριση Άρθρων",
    noArticles: "Δεν υπάρχουν διαθέσιμα άρθρα",
    readMore: "Περισσότερα",
    articleNotFound: "Το άρθρο δεν βρέθηκε",
    backToBlog: "Επιστροφή στο Ιστολόγιο",

    // Rooms
    availableApartments: "Διαθέσιμα Διαμερίσματα",
    availableApartmentsDescription: "Περιηγηθείτε στα διαθέσιμα διαμερίσματα στα κτίριά μας",
    allBuildings: "Όλα τα Κτίρια",
    allTypes: "Όλοι οι Τύποι",
    studio: "Στούντιο",
    oneBedroom: "1 Υπνοδωμάτιο",
    twoBedroom: "2 Υπνοδωμάτια",
    size: "Μέγεθος",
    price: "Τιμή",
    availableFrom: "Διαθέσιμο Από",
    actions: "Ενέργειες",
    inquire: "Ενδιαφέρομαι",
    noRoomsFound: "Δεν βρέθηκαν διαμερίσματα που να ταιριάζουν με τα κριτήριά σας",
    addRoom: "Προσθήκη Διαμερίσματος",
    room: "Διαμέρισμα",
    viewDetails: "Προβολή Λεπτομερειών",

    // Membership Card
    memberSince: "Μέλος Από",
    memberID: "ID Μέλους",
    membershipCardDescription:
      "Παρουσιάστε αυτή την κάρτα σε οποιαδήποτε συνεργαζόμενη επιχείρηση για να ξεκλειδώσετε την αποκλειστική έκπτωση Unity.",
    membershipCardDisclaimer:
      "Αυτή η κάρτα είναι μόνο για σκοπούς αναγνώρισης και παραμένει ιδιοκτησία της UnitySmartLiving.",

    // Discounts
    businessDiscounts: "Εκπτώσεις Επιχειρήσεων",
    cafes: "Καφετέριες",
    restaurants: "Εστιατόρια",
    gyms: "Γυμναστήρια",
    retail: "Λιανική",
    services: "Υπηρεσίες",
    noBusinessesFound: "Δεν βρέθηκαν επιχειρήσεις",
    targetBuilding: "Κτίριο-Στόχος",
    noBuildings: "Χωρίς κτίρια",
    businessName: "Όνομα Επιχείρησης",
    businessCategory: "Κατηγορία Επιχείρησης",
    businessAddress: "Διεύθυνση Επιχείρησης",
    businessPhone: "Τηλέφωνο Επιχείρησης",
    businessDiscount: "Προσφορά Έκπτωσης",
    businessLogo: "Λογότυπο Επιχείρησης",
    availableInBuildings: "Διαθέσιμο Στα Κτίρια",
    selectBuildings: "Επιλέξτε Κτίρια",
    businessDetails: "Λεπτομέρειες Επιχείρησης",
    confirmDeleteBusiness: "Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή την επιχείρηση;",
    businessAdded: "Η επιχείρηση προστέθηκε με επιτυχία",
    businessUpdated: "Η επιχείρηση ενημερώθηε με επιτυχία",
    businessDeleted: "Η επιχείρηση διαγράφηκε με επιτυχία",
    failedToAddBusiness: "Αποτυχία προσθήκης επιχείρησης",
    failedToUpdateBusiness: "Αποτυχία ενημέρωσης επιχείρησης",
    failedToDeleteBusiness: "Αποτυχία διαγραφής επιχείρησης",

    // Notifications
    notifications: "Ειδοποιήσεις",
    markAllAsRead: "Σήμανση όλων ως αναγνωσμένα",
    noNotifications: "Δεν υπάρχουν ειδοποιήσεις",
    notificationPermissionGranted: "تم منح إذن الإشعارات",
    notificationPermissionDenied: "تم رفض إذن الإشعارات",

    // Admin
    totalUsers: "Συνολικοί Χρήστες",
    allUsers: "Όλοι",
    approvedUsers: "Εγκεκρ.",
    disabledUsers: "Απενεργ.",
    duplicateUsers: "Διπλότυπα",
    duplicateUsersHelp: "Ενεργοί ένοικοι στο ίδιο διαμέρισμα, συνήθως παλιός λογαριασμός που πρέπει να απενεργοποιηθεί",
    enableUser: "Επανενεργοποίηση χρήστη",
    enableUserConfirmation: "Επανενεργοποίηση του χρήστη '{{name}}'; Θα μπορεί να συνδεθεί ξανά.",
    userEnabledMessage: "Ο χρήστης επανενεργοποιήθηκε με επιτυχία",
    failedToEnableUser: "Αποτυχία επανενεργοποίησης χρήστη",
    allTickets: "Όλα",
    all: "Όλα",
    searchUsers: "Αναζήτηση χρηστών...",
    searchTickets: "Αναζήτηση αιτημάτων...",
    name: "Όνομα",
    email: "Email",
    role: "Ρόλος",
    user: "Χρήστης",
    admin: "Διαχειριστής",
    approved: "Εγκεκριμένος",
    lastReply: "Τελευταία Απάντηση",
    noone: "Κανείς",
    approveUser: "Έγκριση Χρήστη",
    editUser: "Επεξεργασία Χρήστη",
    deleteUser: "Διαγραφή Χρήστη",
    save: "Αποθήκευση",
    noUsersFound: "Δεν βρέθηκαν χρήστες",
    ticketId: "ID Αιτήματος",
    description: "Περιγραφή",
    noTicketsFound: "Δεν βρέθηκαν αιτήματα",
    view: "Προβολή",
    reopenTicket: "Επανάνοιγμα Αιτήματος",
    editArticle: "Επεξεργασία Άρθρου",
    deleteArticle: "Διαγραφή Άρθρου",
    searchArticles: "Αναζήτηση άρθρων...",
    enterPreview: "Εισάγετε προεπισκόπηση",
    selectCategory: "Επιλέξτε κατηγορία",
    thumbnail: "Εικόνα",
    preview: "Προεπισκόπηση",
    editRoom: "Επεξεργασία Διαμερίσματος",
    deleteRoom: "Διαγραφή Διαμερίσματος",
    searchRooms: "Αναζήτηση διαμερισμάτων...",
    selectBuilding: "Επιλέξτε κτίριο",
    selectType: "Επιλέξτε τύπο",
    editBusiness: "Επεξεργασία Επιχείρησης",
    deleteBusiness: "Διαγραφή Επιχείρησης",
    searchBusinesses: "Αναζήτηση επιχειρήσεων...",
    enterBusinessName: "Εισάγετε όνομα επιχείρησης",
    enterAddress: "Εισάγετε διεύθυνση",
    enterPhone: "Εισάγετε τηλέφωνο",
    enterDiscount: "Εισάγετε έκπτωση",
    logo: "Λογότυπο",
    addBusiness: "Προσθήκη Επιχείρησης",
    failedToApproveUser: "Αποτυχία έγκρισης χρήστη",
    failedToDeleteUser: "Αποτυχία διαγραφής χρήστη",
    failedToUpdateUser: "Αποτυχία ενημέρωσης χρήστη",
    userUpdatedMessage: "Ο χρήστης ενημερώθηκε με επιτυχία",
    confirmDeleteUser: "Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτόν τον χρήστη;",
    saving: "Αποθήκευση...",
    managedBuildings: "Διαχειριζόμενα Κτίρια",
    canReceiveMessages: "Μπορεί να Λάβει Μηνύματα",
    canReceiveMessagesHelp: "Επιτρέψτε σε αυτόν τον διαχειριστή να λαμβάνει μηνύματα από χρήστες",
    managedBuildingsHelp: "Επιλέξτε ποια κτίρια μπορεί να διαχειρίζεται αυτός ο διαχειριστής",
    managedTicketCategories: "Διαχειριζόμενες Κατηγορίες Αιτημάτων",
    managedTicketCategoriesHelp:
      "Επιλέξτε ποιες κατηγορίες αιτημάτων μπορεί να διαχειρίζεται αυτός ο διαχειριστής. Αφήστε κενό για να επιτρέψετε όλες τις κατηγορίες.",
    adminDashboard: "Πίνακας Ελέγχου Διαχειριστή",
    manageBuildings: "Διαχείριση Κτιρίων",
    registrationSettings: "Ρυθμίσεις Εγγραφής",
    registrationCodeAdminHelp:
      "Αυτός ο κωδικός θα απαιτείται για νέες εγγραφές χρηστών. Η σύγκριση κωδικού δεν διακρίνει πεζά από κεφαλαία.",

    // Settings
    profileInformation: "Πληροφορίες Προφίλ",
    saveChanges: "Αποθήκευση Αλλαγών",
    updating: "Ενημέρωση...",
    savePhoto: "Αποθήκευση Φωτογραφίας",

    // Landing Page
    landingTitle: "Έξυπνη Διαβίωση για Σύγχρονες Κοινότητες",
    landingDescription:
      "Διαχειριστείτε την εμπειρία διαμονής σας με ευκολία. Από αιτήματα συντήρησης έως ανακοινώσεις κοινότητας, όλα όσα χρειάζεστε σε ένα μέρος.",
    getStarted: "Ξεκινήστε",
    existingUser: "Υπάρχων Χρήστης",
    featuresTitle: "Χαρακτηριστικά",
    featuresDescription:
      "Ανακαλύψτε πώς το UnitySmartLiving μπορεί να βελτιώσει την εμπειρία διαβίωσής σας με αυτά τα ισχυρά χαρακτηριστικά.",
    featureTicketsTitle: "Αιτήματα Συντήρησης",
    featureTicketsDescription: "Υποβάλετε και παρακολουθήστε αιτήματα συντήρησης εύκολα.",
    featureMessagingTitle: "Άμεση Επικοινωνία",
    featureMessagingDescription: "Επικοινωνήστε απευθείας με τη διαχείριση του κτιρίου.",
    featureAnnouncementsTitle: "Ανακοινώσεις Κοινότητας",
    featureAnnouncementsDescription: "Μείνετε ενημερωμένοι με σημαντικά νέα του κτιρίου.",
    featureBlogTitle: "Ιστολόγιο Κοινότητας",
    featureBlogDescription: "Διαβάστε άρθρα και ενημερώσεις σχετικά με την κοινότητά σας.",
    featureRoomsTitle: "Διαθέσιμα Διαμερίσματα",
    featureRoomsDescription: "Περιηγηθείτε στα διαθέσιμα διαμερίσματα στο κτίριό σας.",
    featureDiscountsTitle: "Τοπικές Εκπτώσεις",
    featureDiscountsDescription: "Απολαύστε ειδικές προσφορές από τοπικές επιχειρήσεις.",
    privacy: "Πολιτική Απορρήτου",
    terms: "Όροι Χρήσης",
    contact: "Επικοινωνία",

    // General
    searchFilter: "Φίλτρο αναζήτησης",
    apply: "Εφαρμογή",
    filter: "Φίλτρο",
    uploadImageDescription: "Ανεβάστε μια εικόνα για να παρέχετε περισσότερες λεπτομέρειες σχετικά με το πρόβλημά σας",
    attachImage: "Επισύναψη Εικόνας",
    fileSelected: "Το αρχείο επιλέχθηκε",
    confirmDelete: "Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτό το στοιχείο;",
    yes: "Ναι",
    no: "Όχι",
    success: "Επιτυχία",
    error: "Σφάλμα",
    warning: "Προειδοποίηση",
    info: "Πληροφορίες",

    // Dashboard
    manageUsers: "Διαχείριση Χρηστών",
    manageTickets: "Διαχείριση Αιτημάτων",
    manageAnnouncements: "Διαχείριση Ανακοινώσεων",
    manageBlog: "Διαχείριση Ιστολογίου",
    manageRoomsTitle: "Διαχείριση Διαμερισμάτων",
    manageDiscounts: "Διαχείριση Εκπτώσεων",
    recentActivities: "Πρόσφατες Δραστηριότητες",
    allActivities: "Όλες οι Δραστηριότητες",
    viewAll: "Προβολή Όλων",
    accountSettings: "Ρυθμίσεις Λογαριασμού",
    personalInformation: "Προσωπικές Πληροφορίες",
    emailAddressLabel: "Διεύθυνση Email",
    phoneNumberLabel: "Αριθμός Τηλεφώνου",

    // Tickets
    ticketDetails: "Λεπτομέρειες Αιτήματος",
    ticketStatus: "Κατάσταση Αιτήματος",
    ticketHistory: "Ιστορικό Αιτήματος",
    commentContent: "Περιεχόμενο σχολίου",
    submitComment: "Υποβολή Σχολίου",
    ticketImages: "Εικόνες Αιτήματος",

    // Messages
    messageDetails: "Λεπτομέρειες Μηνύματος",
    replyToMessage: "Απάντηση στο Μήνυμα",
    markAsRead: "Σημείωση ως Αναγνωσμένο",
    markAsUnread: "Σημείωση ως Μη Αναγνωσμένο",
    deleteConversation: "Διαγραφή Συνομιλίας",
    newConversation: "Νέα Συνομιλία",
    messageManagement: "Διαχείριση Μηνυμάτων",

    // Rooms
    buildingLabel: "Κτίριο",
    roomTypeLabel: "Τύπος Διαμερίσματος",
    roomSizeLabel: "Μέγεθος Διαμερίσματος",
    roomPriceLabel: "Τιμή Διαμερίσματος",
    availabilityDate: "Ημερομηνία Διαθεσιμότητας",
    inquireAboutRoom: "Πληροφορίες για το Διαμέρισμα",
    inquiryDetails: "Λεπτομέρειες Αιτήματος",
    inquirySent: "Το Αίτημα Στάλθηκε",
    submitInquiry: "Υποβολή Αιτήματος",

    // Notification Messages
    notificationSuccessTitle: "Επιτυχής Ενέργεια",
    notificationErrorTitle: "Αποτυχημένη Ενέργεια",
    userApprovedMessage: "Ο χρήστης εγκρίθηκε με επιτυχία",
    userDeletedMessage: "Ο χρήστης διαγράφηκε με επιτυχία",
    profileUpdatedMessage: "Το προφίλ ενημερώθηκε με επιτυχία",
    passwordChangedMessage: "Ο κωδικός άλλαξε με επιτυχία",
    photoUpdatedMessage: "Η φωτογραφία προφίλ ενημερώθηκε με επιτυχία",
    roomAddedMessage: "Το διαμέρισμα προστέθηκε με επιτυχία",
    roomUpdatedMessage: "Το διαμέρισμα ενημερώθηκε με επιτυχία",
    roomDeletedMessage: "Το διαμέρισμα διαγράφηκε με επιτυχία",
    ticketSubmittedMessage: "Το αίτημα υποβλήθηκε με επιτυχία",
    ticketUpdatedMessage: "Το αίτημα ενημερώθηκε με επιτυχία",
    ticketClosedMessage: "Το αίτημα έκλεισε με επιτυχία",
    ticketReopenedMessage: "Το αίτημα άνοιξε ξανά με επιτυχία",
    commentAddedMessage: "Το σχόλιο προστέθηκε με επιτυχία",

    // Form Validation
    requiredField: "Αυτό το πεδίο είναι υποχρεωτικό",
    invalidEmail: "Παρακαλώ εισάγετε έγκυρη διεύθυνση email",
    passwordTooShort: "Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες",
    passwordsMustMatch: "Οι κωδικοί πρέπει να ταιριάζουν",
    invalidPhoneNumber: "Παρακαλώ εισάγετε έγκυρο αριθμό τηλεφώνου",
    required: "Αυτό το πεδίο είναι υποχρεωτικό",
    passwordsDoNotMatch: "Οι κωδικοί δεν ταιριάζουν",

    // Admin Operations
    changeUserRole: "Αλλαγή Ρόλου Χρήστη",
    approveSelectedUsers: "Έγκριση Επιλεγμένων Χρηστών",
    bulkActions: "Μαζικές Ενέργειες",
    createNewAnnouncement: "Δημιουργία Νέας Ανακοίνωσης",
    createNewArticle: "Δημιουργία Νέου Άρθρου",
    publishArticle: "Δημοσίευση Άρθρου",
    saveDraft: "Αποθήκευση Προσχεδίου",
    previewArticle: "Προεπισκόπηση Άρθρου",
    targetBuildings: "Κτίρια-Στόχοι",
    notifyUsers: "Ειδοποίηση Χρηστών",
    sendNotification: "Αποστολή Ειδοποίησης",
    notificationSent: "Η ειδοποίηση στάλθηκε με επιτυχία",
    exportData: "Εξαγωγή Δεδομένων",
    importData: "Εισαγωγή Δεδομένων",
    systemSettings: "Ρυθμίσεις Συστήματος",
    emailTemplates: "Πρότυπα Email",
    attachFile: "Επισύναψη Αρχείου",
    notificationOptions: "Επιλογές Ειδοποιήσεων",
    sendEmailNotification: "Αποστολή Ειδοποίησης Email",
    enablePushNotification: "Ενεργοποίηση Push Ειδοποίησης",
    pushNotificationInfo: "Οι push ειδοποιήσεις θα σταλούν σε όλους τους χρήστες στα επιλεγμένα κτίρια.",
    emailNotificationInfo: "Οι ειδοποιήσεις email θα σταλούν σε όλους τους χρήστες στα επιλεγμένα κτίρια.",
    showExpandedOnHome: "Εμφάνιση ολόκληρης στην αρχική",
    expandAnnouncement: "Εμφάνιση ολόκληρης ανακοίνωσης",
    collapseAnnouncement: "Σύμπτυξη ανακοίνωσης",
    showLess: "Λιγότερα",
    category: "Κατηγορία",
    address: "Διεύθυνση",
    discount: "Έκπτωση",

    searchBuildings: "Αναζήτηση κτιρίων",
    profilePhotoRequired: "Η φωτογραφία προφίλ είναι υποχρεωτική",

    // Reports and Statistics
    statistics: "Στατιστικά",
    reports: "Αναφορές",
    occupancyRate: "Ποσοστό Πληρότητας",
    ticketResolutionTime: "Χρόνος Επίλυσης Αιτημάτων",
    activeUsers: "Ενεργοί Χρήστες",
    newRegistrations: "Νέες Εγγραφές",
    monthlyReport: "Μηνιαία Αναφορά",
    yearlyReport: "Ετήσια Αναφορά",
    customReport: "Προσαρμοσμένη Αναφορά",
    generateReport: "Δημιουργία Αναφοράς",
    exportToPDF: "Εξαγωγή σε PDF",
    exportToCSV: "Εξαγωγή σε CSV",
    dataRange: "Εύρος Δεδομένων",
    chartType: "Τύπος Γραφήματος",
    pieChart: "Κυκλικό Διάγραμμα",
    barChart: "Ραβδόγραμμα",
    lineChart: "Γραμμικό Διάγραμμα",

    // Memberships and Payments
    membershipDetails: "Λεπτομέρειες Συνδρομής",
    membershipLevel: "Επίπεδο Συνδρομής",
    membershipBenefits: "Οφέλη Συνδρομής",
    paymentHistory: "Ιστορικό Πληρωμών",
    makePayment: "Πραγματοποίηση Πληρωμής",
    paymentMethod: "Μέθοδος Πληρωμής",
    creditCard: "Πιστωτική Κάρτα",
    bankTransfer: "Τραπεζική Μεταφορά",
    paypal: "PayPal",
    paymentAmount: "Ποσό Πληρωμής",
    paymentDate: "Ημερομηνία Πληρωμής",
    paymentStatus: "Κατάσταση Πληρωμής",
    paid: "Πληρώθηκε",
    pending: "Σε Εκκρεμότητα",
    failed: "Απέτυχε",
    refunded: "Επιστροφή Χρημάτων",
    invoices: "Τιμολόγια",
    viewInvoice: "Προβολή Τιμολογίου",
    downloadInvoice: "Λήψη Τιμολογίου",
    billingAddress: "Διεύθυνση Χρέωσης",
    subscriptionPlan: "Πλάνο Συνδρομής",
    changeSubscription: "Αλλαγή Συνδρομής",
    cancelSubscription: "Ακύρωση Συνδρομής",

    // Calendar and Events
    calendar: "Ημερολόγιο",
    upcomingEvents: "Επερχόμενες Εκδηλώσεις",
    createEvent: "Δημιουργία Εκδήλωσης",
    eventTitle: "Τίτλος Εκδήλωσης",
    eventDescription: "Περιγραφή Εκδήλωσης",
    eventDate: "Ημερομηνία Εκδήλωσης",
    startTime: "Ώρα Έναρξης",
    endTime: "Ώρα Λήξης",
    eventLocation: "Τοποθεσία Εκδήλωσης",
    eventType: "Τύπος Εκδήλωσης",
    communityEvent: "Εκδήλωση Κοινότητας",
    privateMeeting: "Ιδιωτική Συνάντηση",
    maintenanceWork: "Εργασίες Συντήρησης",
    allDayEvent: "Ολοήμερη Εκδήλωση",
    recurringEvent: "Επαναλαμβανόμενη Εκδήλωση",
    eventParticipants: "Συμμετέχοντες Εκδήλωσης",
    inviteParticipants: "Πρόσκληση Συμμετεχόντων",
    rsvp: "Απάντηση Πρόσκλησης",
    attending: "Θα Παρευρεθώ",
    notAttending: "Δεν Θα Παρευρεθώ",
    maybeAttending: "Ίσως Παρευρεθώ",

    // Buildings
    addBuilding: "Προσθήκη Κτιρίου",
    editBuilding: "Επεξεργασία Κτιρίου",
    archiveBuilding: "Αρχειοθέτηση Κτιρίου",
    unarchiveBuilding: "Επαναφορά Κτιρίου",
    showArchived: "Εμφάνιση Αρχειοθετημένων",
    noBuildingsFound: "Δεν βρέθηκαν κτίρια",
    enterBuildingName: "Εισάγετε όνομα κτιρίου",
    enterCity: "Εισάγετε πόλη",
    enterZipCode: "Εισάγετε ταχυδρομικό κώδικα",
    enterTotalRooms: "Εισάγετε συνολικό αριθμό διαμερισμάτων",
    totalRooms: "Συνολικά Διαμερισμάτα",
    zipCode: "Ταχ. Κώδικας",
    city: "Πόλη",
    active: "Ενεργό",
    archived: "Αρχειοθετημένο",
    allStatuses: "Όλες οι Καταστάσεις",
    disabled: "Απενεργοποιημένο",
    businessActive: "Επιχείρηση Ενεργή",
    enableBusiness: "Ενεργοποίηση Επιχείρησης",
    disableBusiness: "Απενεργοποίηση Επιχείρησης",

    // Logout confirmation
    confirmLogout: "Είστε βέβαιοι ότι θέλετε να αποσυνδεθείτε;",
    areYouSureLogout: "Είστε βέβαιοι ότι θέλετε να αποσυνδεθείτε;",

    // Message status
    unread: "Μη Αναγνωσμένο",

    // Delete confirmations
    deleteConversationConfirmation: "Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή τη συνομιλία;",
    deleteTicketConfirmation: "Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτό το αίτημα '{{title}}';",
    deleteAnnouncementConfirmation: "Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή την ανακοίνωση '{{title}}';",
    deleteArticleConfirmation: "Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτό το άρθρο '{{title}}';",
    deleteBusinessConfirmation: "Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή την επιχείρηση '{{name}}';",
    deleteUserConfirmation: "Είστε βέβαιοι ότι θέλετε να διαγράψετε τον χρή '{{name}}';",
    archiveBuildingConfirmation: "Είστε βέβαιοι ότι θέλετε να αρχειοθετήσετε το κτίριο '{{name}}';",
    unarchiveBuildingConfirmation: "Είστε βέβαιοι ότι θέλετε να επαναφέρετε το κτίριο '{{name}}';",

    // General actions
    delete: "Διαγραφή",
    archive: "Αρχειοθέτηση",
    unarchive: "Επαναφορά",
    confirmArchive: "Επιβεβαίωση Αρχειοθέτησης",
    confirmUnarchive: "Επιβεβαίωση Επαναφοράς",

    // Password Reset
    passwordResetEmailSent:
      "Το email επαναφοράς κωδικού στάλθηκε με επιτυχία. Παρακαλώ ελέγξτε το email σας για οδηγίες.",
    failedToSendResetEmail: "Αποτυχία αποστολής email επαναφοράς κωδικού",
    noAccountFound: "Δεν βρέθηκε λογαριασμός με αυτή τη διεύθυνση email",
    tooManyRequests: "Πάρα πολλά αιτήματα επαναφοράς κωδικού. Παρακαλώ δοκιμάστε ξανά αργότερα",
    networkError: "Σφάλμα δικτύου. Παρακαλώ ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά",
    serverError: "Σφάλμα στον διακομιστή. Παρακαλώ δοκιμάστε ξανά αργότερα.",
    unauthorized: "Δεν έχετε άδεια να εκτελέσετε αυτή την ενέργεια.",

    // Account page
    account: "Λογαριασμός",
    accountInformation: "Πληροφορίες Λογαριασμού",
    deleteMyAccount: "Διαγραφή του λογαριασμού μου",
    deleteAccountConfirmation: "Διαγραφή Λογαριασμού",
    deleteAccountWarning:
      "Είστε βέβαιοι ότι θέλετε να ζητήσετε τη διαγραφή του λογαριασμού; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.",
    deleteAccountProcess:
      "Οι διαχειριστές θα ειδοποιηθούν και θα επικοινωνήσουν μαζί σας εντός 24 ωρών για να επεξεργαστούν το αίτημά σας.",
    requestAccountDeletion: "Αίτημα Διαγραφής Λογαριασμού",
    requestSubmitted: "Το Αίτημα Υποβλήθηκε",
    accountDeletionRequestMessage:
      "Οι διαχειριστές έχουν ενημερωθεί και θα επικοινωνήσουν μαζί σας τις επόμενες 24 ώρες.",
    failedToRequestAccountDeletion: "Αποτυχία υποβολής αιτήματος διαγραφής λογαριασμού",
    notProvided: "Δεν παρέχεται",
    pendingApproval: "Εκκρεμείς Εγκρίσεις",
    accountPendingMessage:
      "Ο λογαριασμός σας αναμένει έγκριση. Παρακαλώ περιμένετε για μια εγκρίση από έναν διαχειριστή.",
    contactAdmin: "Επικοινωνία με τον Διαχειριστή",

    members: "μέλη",

    somethingWentWrong: "Κάτι πήγε στραβά",
    unknownError: "Άγνωστο σφάλμα",
    loginSuccessful: "Επιτυχής σύνδεση! Καλώς ήρθατε πίσω στο UnitySmartLiving!",
    invalidEmailOrPassword: "Μη έγκυρο email ή κωδικός",
    registrationSuccessful: "Επιτυχής εγγραφή! Ο λογαριασμός σας αναμένει έγκριση.",
    maxDimensions: "Μέγιστες διαστάσεις",
    switchLanguage: "Αλλαγή γλώσσας",

    emailPlaceholder: "name@example.com",
    roomSizePlaceholder: "45τμ",

    newMessageNotification: "Νέο Μήνυμα",
    newMessageContent: "Έχετε ένα νέο μήνυμα από τον Διαχειριστή",
    ticketUpdateNotification: "Ενημέρωση Αιτήματος",
    ticketUpdateContent: "Το αίτημα συντήρησής σας έχει ενημερωθεί",
    buildingAnnouncementNotification: "Ανακοίνωση Κτιρίου",
    buildingAnnouncementContent: "Σημαντικό: Συντήρηση προγραμματισμένη για αύριο",

    adminRole: "Διαχειριστής",
    adminUsers: "Διαχειριστές",
    maintenanceTeam: "Ομάδα Συντήρησης",
    buildingManager: "Διαχειριστής Κτιρίου",
    securityTeam: "Ομάδα Ασφαλείας",

    // Attachment Preview Modal
    download: "Λήψη",
    close: "Κλείσιμο",
    fileType: "Τύπος Αρχείου",
    previewNotAvailable: "Η προεπισκόπηση δεν είναι διαθέσιμη",
    downloadToView: "Κάντε λήψη για προβολή",

    // Guest Registration
    guestRegistration: "Δήλωση Φιλοξενούμενου",
    guestManagement: "Διαχείριση Φιλοξενούμενων",
    registerGuest: "Δήλωση Φιλοξενούμενου",
    parents: "Γονείς",
    guestFullName: "Ονοματεπώνυμο",
    guestIdNumber: "Αριθμός Ταυτότητας",
    checkInDate: "Ημερομηνία Άφιξης",
    createdAt: "Δημιουργήθηκε",
    checkOutDate: "Ημερομηνία Αναχώρησης",
    checkOutAfterCheckIn: "Η ημερομηνία αναχώρησης πρέπει να είναι μετά την ημερομηνία άφιξης",
    checkInPastWarning: "Προσοχή: Η ημερομηνία άφιξης είναι στο παρελθόν.",
    totalDays: "Σύνολο Ημερών",
    daysUsedThisMonth: "Ημέρες Χρήσης Αυτόν τον Μήνα",
    monthlyLimit: "Μηνιαίο Όριο",
    daysPerMonth: "ημέρες/μήνα",
    guestHistory: "Ιστορικό Φιλοξενούμενων",
    noGuestsFound: "Δεν βρέθηκαν εγγραφές φιλοξενούμενων",
    guestRegisteredSuccessfully: "Ο φιλοξενούμενος καταχωρήθηκε επιτυχώς",
    guestRegistrationFailed: "Αποτυχία καταχώρησης φιλοξενούμενου",
    guestDeletedSuccessfully: "Η εγγραφή φιλοξενούμενου διαγράφηκε",
    limitWarning: "Προσοχή: Αυτή η εγγραφή θα φέρει το σύνολό σας στις {{total}} ημέρες αυτόν τον μήνα, υπερβαίνοντας το όριο των {{limit}} ημερών. Ενδέχεται να ισχύουν επιπλέον χρεώσεις.",
    historyDisabledMessage: "Το ιστορικό φιλοξενούμενων δεν είναι διαθέσιμο για αυτό το κτίριο.",
    guestSettings: "Ρυθμίσεις Φιλοξενίας",
    monthlyDayLimit: "Μηνιαίο Όριο Ημερών",
    zeroMeansNoLimit: "0 = χωρίς όριο",
    showLimitWarning: "Εμφάνιση Προειδοποίησης Ορίου στους Ενοίκους",
    showGuestHistory: "Εμφάνιση Ιστορικού Φιλοξενούμενων στους Ενοίκους",
    saveSettings: "Αποθήκευση Ρυθμίσεων",
    settingsSaved: "Οι ρυθμίσεις αποθηκεύτηκαν επιτυχώς",
    settingsSaveFailed: "Αποτυχία αποθήκευσης ρυθμίσεων",
    notificationSettings: "Ρυθμίσεις Ειδοποιήσεων",
    muteGuestNotifications: "Σίγαση ειδοποιήσεων φιλοξενίας",
    muteGuestNotificationsHelp: "Όταν είναι ενεργοποιημένο, δεν θα λαμβάνετε email ή push ειδοποιήσεις για νέες δηλώσεις φιλοξενίας, υπερβάσεις ορίου ή σχόλια.",
    disableEmailNotifications: "Απενεργοποίηση ειδοποιήσεων email",
    disableEmailNotificationsHelp: "Όταν είναι ενεργοποιημένο, αυτός ο διαχειριστής δεν θα λαμβάνει ειδοποιήσεις email. Οι push ειδοποιήσεις εξακολουθούν να αποστέλλονται.",
    selectMonth: "Επιλογή Μήνα",
    monthlySummary: "Μηνιαία Σύνοψη",
    totalGuests: "Σύνολο Φιλοξενούμενων",
    apartment: "Διαμέρισμα",
    resident: "Ένοικος",
    numberOfGuests: "Αριθμός Φιλοξενούμενων",
    guestRegistrations: "Εγγραφές Φιλοξενούμενων",
    exceedsLimit: "υπέρβαση ορίου",
    selectBuildingFirst: "Παρακαλώ επιλέξτε πρώτα κτίριο",
    allFieldsRequired: "Όλα τα πεδία είναι υποχρεωτικά",
    notes: "Σημειώσεις",
    notesPlaceholder: "Προαιρετικές σημειώσεις για τον φιλοξενούμενο...",
    allApartments: "Όλα τα Διαμερίσματα",
    showOverlapsOnly: "Εμφάνιση Μόνο Επικαλύψεων",
    overlappingBooking: "Αλληλεπικαλυπτόμενη κράτηση",
    overlapWarning: "Προσοχή: Αυτό το διαμέρισμα έχει ήδη φιλοξενούμενο για επικαλυπτόμενες ημερομηνίες.",
    thread: "Συζήτηση",
    openThread: "Άνοιγμα Συζήτησης",
    noCommentsYet: "Δεν υπάρχουν σχόλια ακόμα",
    startConversation: "Ξεκινήστε τη συζήτηση",
    addComment: "Προσθήκη Σχολίου",
    typeYourMessage: "Γράψτε το μήνυμά σας",
    conversation: "Συζήτηση",
    guestNotFound: "Η εγγραφή φιλοξενίας δεν βρέθηκε",
    days: "ημέρες",
    // SoftOne links (admin only)
    softoneColumn: "SoftOne",
    softoneLinkLabel: "Σύνδεση SoftOne",
    softonePending: "Εκκρεμεί",
    softonePendingOption: "— Εκκρεμεί (δεν έχει αποφασιστεί) —",
    softoneNone: "Χωρίς σύνδεση SoftOne",
    softoneNoneShort: "Χωρίς σύνδεση",
    softoneSuggested: "Πρόταση",
    softoneUseSuggestion: "Χρήση πρότασης",
    softoneDetailCode: "Κωδικός",
    softoneDetailName: "Περιγραφή",
    softoneDetailContract: "Σύμβαση",
    softoneDetailMatch: "Ταίριασμα",
    softoneDetailSource: "Τρόπος σύνδεσης",
    softoneDetailBy: "Από",
    softoneDetailAt: "Πότε",
    softoneSource_suggestion: "έγκριση πρότασης",
    softoneSource_manual: "χειροκίνητη επιλογή",
    softoneSource_backfill: "μαζική σύνδεση",
    softoneChoose: "Επιλογή",
    softoneSearchPlaceholder: "Αναζήτηση με κωδικό ή όνομα...",
    softoneNoResults: "Δεν βρέθηκαν εγγραφές SoftOne",
    softoneApproveSuggestion: "Έγκριση πρότασης",
    softoneConfidence_exact: "ακριβές ταίριασμα",
    softoneConfidence_normalized: "ταίριασμα μετά από κανονικοποίηση",
    softoneConfidence_fuzzy: "κατά προσέγγιση — ελέγξτε",
    softoneStale: "Το κτίριο ή ο αριθμός διαμερίσματος του χρήστη άλλαξε μετά τη σύνδεση, ή το διαμέρισμα δεν υπάρχει πια στο SoftOne. Ελέγξτε τη σύνδεση.",
    softoneMissing: "Το συνδεδεμένο κτίριο δεν υπάρχει πια στο SoftOne. Ελέγξτε τη σύνδεση.",
    softoneBuildingNotLinked: "Συνδέστε πρώτα το κτίριο του χρήστη με το SoftOne (Διαχείριση Κτιρίων).",
    softoneBuildingNoLink: "Το κτίριο του χρήστη έχει οριστεί χωρίς σύνδεση SoftOne.",
    softoneLoading: "Φόρτωση δεδομένων SoftOne...",
    softoneLoadError: "Δεν ήταν δυνατή η φόρτωση δεδομένων SoftOne",
    softoneLinkSaved: "Η σύνδεση SoftOne αποθηκεύτηκε",
    softoneLinkSaveError: "Αποτυχία αποθήκευσης σύνδεσης SoftOne",
    softoneAdminOnlyHelp: "Εμφανίζεται μόνο στους διαχειριστές.",
    softoneFilterAll: "SoftOne: όλοι",
    softoneFilterLinked: "Συνδεδεμένοι",
    softoneFilterSuggested: "Με πρόταση",
    softoneFilterPending: "Εκκρεμεί, χωρίς πρόταση",
    softoneFilterNone: "Χωρίς σύνδεση",
    softoneFilterStale: "Θέλουν έλεγχο",
    softoneRequestColumn: "SoftOne",
    softoneRequestSent: "Αίτημα #{{id}}",
    softoneRequestSending: "Αποστέλλεται...",
    softoneRequestFailed: "Απέτυχε",
    softoneRequestWaiting: "Χωρίς διαμέρισμα",
    softoneRequestWaitingHelp: "Ο χρήστης δεν έχει συνδεδεμένο διαμέρισμα SoftOne. Συνδέστε το στη Διαχείριση Χρηστών και στείλτε ξανά.",
    softoneRequestDisabled: "Δεν στάλθηκε",
    softoneRequestDisabledHelp: "Η αποστολή στο SoftOne ήταν απενεργοποιημένη όταν δημιουργήθηκε το αίτημα.",
    softoneRequestSend: "Αποστολή στο SoftOne",
    softoneRequestSentToast: "Στάλθηκε στο SoftOne",
    softoneRequestSendError: "Αποτυχία αποστολής στο SoftOne",
  },
}

// Create context
interface LanguageContextType {
  language: string
  setLanguage: (lang: string) => void
  t: (key: string, params?: Record<string, string>) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

// Provider component
export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState(() => {
    // Initialize from localStorage if available
    if (typeof window !== "undefined") {
      return localStorage.getItem("language") || "el"
    }
    return "el"
  })

  // Save language to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("language", language)
    }
  }, [language])

  const t = (key: string, params?: Record<string, string>) => {
    const translation = translations[language as keyof typeof translations]
    let text = translation[key as keyof typeof translation] || key

    if (params) {
      Object.keys(params).forEach((param) => {
        text = text.replace(`{{${param}}}`, params[param])
      })
    }

    return text
  }

  return <LanguageContext.Provider value={{ language, setLanguage, t }}>{children}</LanguageContext.Provider>
}

// Hook to use the translation context
export function useTranslation() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useTranslation must be used within an I18nProvider")
  }
  return context
}

// Hook to use just the language
export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within an I18nProvider")
  }
  return { language: context.language, setLanguage: context.setLanguage }
}
